import clsx from 'clsx'
import { CommentHubAuthContext } from 'shared-components/contexts/AuthContext'
import { avatarFill, avatarInitials, ownShownIdentity, youState } from 'lib/community/model'
import { AvatarPhoto } from './TierAvatar'
import { YouCardView } from './YouCard'
import AccountMenuStats from './AccountMenuStats'
import styles from './AccountMenuIdentity.module.css'

/** The header disc's drawn size (the header and the slim head are 32px). */
const LETTER_PX = 32

/**
 * The reader's disc in the header and the menu's slim head: the member hub's letter disc
 * (TierAvatar's fill + initials), keyed on the hub handle when there is one, else the comment
 * name — with the reader's own commentHub picture over it when they have one. (A member's menu
 * head is the You card, which shows the photo only when they show their name, contract B.3.) (Directive
 * 980fe148: "swap LetterAvatar, not the call sites"; identity ticket §3.3). This surface is the
 * reader's own and never in a shared-cached HTML: the picture comes from the reader's session
 * (`CommentHubAuthContext`, absent outside the provider and during SSR), unlike the community's
 * `TierAvatar`, whose photo is the member's public `avatar_url`. A failed photo leaves the letter.
 */
export const LetterAvatar: React.VFC<{ handle: string | null; name: string }> = ({
  handle,
  name,
}) => {
  const key = handle || name
  const picture = CommentHubAuthContext.useOptionalContainer()?.authState?.user?.avatarUrl ?? null
  const photo = picture && <AvatarPhoto key={picture} src={picture} px={LETTER_PX} />
  if (!key) {
    // Not known yet (session still loading): a quiet empty disc, never a "?" that flips later —
    // but a picture the session already has still shows (no letter under it to fall back to).
    return <span className={clsx(styles.letter, styles.letterEmpty)}>{photo}</span>
  }
  return (
    <span className={styles.letter} style={{ backgroundColor: avatarFill(key) }}>
      {avatarInitials(key)}
      {photo}
    </span>
  )
}

/**
 * The avatar menu's head (Member Hub 2a). A reader with a member page gets the You card's menu
 * layout — handle, tier · points, "View page →", the progress line (Directive 7c4e6537: no fifth
 * points card; no disc since Member Stats v2) — and under it the stats glance (Amendment B.2,
 * `AccountMenuStats`, read when the menu opens); anyone else a slim row with their comment name.
 * `me` is the menu's lazily-read `/me` — or, from the second page on, the head remembered from the
 * last answer (`useMemberNotificationLinks`), until the open re-reads it; null without an owner
 * device.
 */
export const AccountMenuIdentity: React.VFC<{
  me: Parameters<typeof youState>[0]
  hubHandle: string | null
  /** The remembered hub handle for the letter disc (same as the header's). */
  avatarHandle?: string | null
  name: string
  profileHref: string
  trailingSlash: boolean
  /** How many times the menu has opened (0 = not yet): the stats glance reads on an open only. */
  opened?: number
}> = ({ me, hubHandle, avatarHandle = null, name, profileHref, trailingSlash, opened = 0 }) => {
  if (me && hubHandle) {
    return (
      <div className={styles.head}>
        <YouCardView
          state={youState(me)}
          layout="menu"
          settingsHref={profileHref}
          trailingSlash={trailingSlash}
          onSignIn={() => undefined}
          shown={ownShownIdentity(me)}
        />
        <AccountMenuStats
          handle={hubHandle}
          opened={opened}
          standing={me.community ?? null}
          reviews={me.shelf.filter(s => s.note.status === 'published').length}
        />
      </div>
    )
  }
  if (!me && avatarHandle) {
    // A known member whose /me is still on its way: hold the You card's place (no slim row that
    // then jumps to the card).
    return <div className={styles.pending} aria-busy="true" aria-label="Loading your profile" />
  }
  return (
    <div className={styles.slim}>
      <span className={styles.avatar} aria-hidden="true">
        <LetterAvatar handle={avatarHandle ?? hubHandle} name={name} />
      </span>
      <b className={styles.name}>{name || 'Your account'}</b>
    </div>
  )
}

/** The header's avatar — the same letter disc as the menu and the member hub. */
export const HeaderAvatar: React.VFC<{ handle: string | null; name: string }> = props => (
  <LetterAvatar {...props} />
)
